/**
 * What can be done to a group: **rename** it and **delete** it.
 *
 * Only `GroupDetailPage` renders these today. `GroupsPage` is a card grid with
 * no action slot, so a card has nothing to mirror into; the declaration is here
 * anyway, so a future table of groups gets the same set by asking for it.
 *
 * Deleting **cascades**: memberships, roles given to the group and its grants on
 * files and folders all go with it. The confirmation loads those counts first
 * and says them, because a group's grants are spread over the whole tree and no
 * other screen shows them. One thing blocks the delete instead — a notification
 * rule whose audience is the group, which would otherwise notify nobody,
 * silently. The server refuses that case with 409 `group_in_use` as well.
 *
 * "Add member" is not here: it acts on the membership panel, a relationship,
 * not on the record. See `./README.md`.
 */
import { useTranslation } from 'react-i18next';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import DialogContentText from '@mui/material/DialogContentText';
import TextField from '@mui/material/TextField';
import type { GroupDto, GroupUsage } from '@forsys/shared';
import { deleteGroup, getGroupUsage, updateGroup } from '../../api/identity';
import { useSelfLockout, withLockoutDialog } from '../../auth/useSelfLockout';
import { BLANK_NAMED_FORM, namedChanges, namedFormFor, type NamedForm } from '../../utils/namedForm';
import {
  confirmAction,
  formAction,
  useEntityActions,
  type EntityActionsState,
} from '../../hooks/useEntityActions';

/** A group's name and description — the same form a role has. */
export type GroupForm = NamedForm;

export const BLANK_GROUP_FORM: GroupForm = BLANK_NAMED_FORM;

/**
 * The fields, exported so `GroupsPage`'s create dialog renders the identical
 * ones from its own state. Creating is the screen's act, not the entity's.
 */
export function useGroupFields() {
  const { t } = useTranslation('people');

  return (form: GroupForm, patch: (changes: Partial<GroupForm>) => void) => (
    <>
      <TextField
        label={t('groups.createDialog.name')}
        value={form.name}
        onChange={(event) => patch({ name: event.target.value })}
        autoFocus
        fullWidth
      />
      <TextField
        label={t('groups.createDialog.description')}
        value={form.description}
        onChange={(event) => patch({ description: event.target.value })}
        multiline
        minRows={2}
        fullWidth
      />
    </>
  );
}

/** What goes with the group, in the delete confirmation. */
function GroupDeleteMessage({ usage }: { usage: GroupUsage }) {
  const { t } = useTranslation('people');
  const lines = [
    usage.members > 0 && t('groups.deleteDialog.members', { count: usage.members }),
    usage.roleAssignments > 0 &&
      t('groups.deleteDialog.roleAssignments', { count: usage.roleAssignments }),
    usage.resourceGrants > 0 &&
      t('groups.deleteDialog.resourceGrants', { count: usage.resourceGrants }),
  ].filter((line): line is string => typeof line === 'string');

  return (
    <>
      {lines.length > 0 ? (
        <>
          <DialogContentText>{t('groups.deleteDialog.intro')}</DialogContentText>
          <Box component="ul" sx={{ mt: 0.5, mb: 1.5, pl: 3 }}>
            {lines.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </Box>
        </>
      ) : (
        <DialogContentText sx={{ mb: 1.5 }}>{t('groups.deleteDialog.nothing')}</DialogContentText>
      )}
      <DialogContentText>{t('groups.deleteDialog.consequence')}</DialogContentText>
    </>
  );
}

export interface UseGroupActionsOptions {
  /** After a successful rename. Hand the record over. */
  onChanged: (group: GroupDto) => void;
  /** After a delete. Leave the screen, or reload the list. */
  onDeleted: (group: GroupDto) => void;
}

export function useGroupActions(options: UseGroupActionsOptions): EntityActionsState<GroupDto> {
  const { t } = useTranslation('people');
  const fields = useGroupFields();
  const lockout = useSelfLockout();

  const state = useEntityActions<GroupDto>(
    [
      formAction<GroupDto, GroupForm>({
        kind: 'form',
        key: 'rename',
        label: t('groups.edit'),
        icon: <EditOutlinedIcon fontSize="small" />,
        permission: 'group.update',
        blank: BLANK_GROUP_FORM,
        formFor: namedFormFor,
        fields,
        incomplete: (form) => form.name.trim() === '',
        title: (group) => t('groups.editDialog.title', { name: group.name }),
        submitLabel: t('groups.editDialog.submit'),

        run: async (group, form) => {
          const changes = namedChanges(group, form);
          // The server refuses an empty PATCH rather than auditing nothing.
          if (Object.keys(changes).length === 0) return { group };
          return updateGroup(group.id, changes);
        },
      }),

      confirmAction<GroupDto, GroupUsage>({
        kind: 'confirm',
        key: 'delete',
        label: t('groups.delete'),
        icon: <DeleteOutlinedIcon fontSize="small" />,
        permission: 'group.delete',
        tone: 'error',
        title: (group) => t('groups.deleteDialog.title', { name: group.name }),
        load: async (group) => (await getGroupUsage(group.id)).usage,
        message: (_group, usage) => <GroupDeleteMessage usage={usage} />,
        blocked: (_group, usage) =>
          usage.notificationRules > 0
            ? t('groups.deleteDialog.blocked', { count: usage.notificationRules })
            : null,
        confirmLabel: t('groups.deleteDialog.confirm'),
        run: async (group) => {
          // Deleting a group you are in can take your own permissions with it.
          await lockout.guard((accept) => deleteGroup(group.id, accept));
          return { deleted: group };
        },
      }),
    ],
    {
      onChanged: (result, key) => {
        if (key === 'delete') options.onDeleted((result as { deleted: GroupDto }).deleted);
        else options.onChanged((result as { group: GroupDto }).group);
      },
    },
  );

  return withLockoutDialog(state, lockout.dialog);
}
